import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { createClient, SupabaseClient } from '@supabase/supabase-js';

// Robust environment variable accessor for Vite
const getEnv = (key: string, fallback: string): string => {
  try {
    // @ts-ignore
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      // @ts-ignore
      const viteEnv = import.meta.env[key] || import.meta.env[`VITE_${key}`];
      if (viteEnv) return viteEnv;
    }
    
    // Fallback to process.env if available (Node/Cloudflare)
    // @ts-ignore
    if (typeof process !== 'undefined' && process.env) {
      // @ts-ignore
      const processEnv = process.env[key];
      if (processEnv) return processEnv;
    }
  } catch (e) {}
  return fallback;
};

// Provided Supabase Credentials
const SUPABASE_URL = getEnv('SUPABASE_URL', 'https://fffivpclttrjazhzxkue.supabase.co');
const SUPABASE_ANON_KEY = getEnv('SUPABASE_ANON_KEY', 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZmZml2cGNsdHRyamF6aHp4a3VlIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjgyMzE5MDksImV4cCI6MjA4MzgwNzkwOX0.alIGvKB8vjmO8fxwSPcvhHZJULo49IyYNW0qWFV_7dc');

// Initialize client with validation
let supabase: SupabaseClient | null = null;
if (SUPABASE_URL && SUPABASE_URL.startsWith('http')) {
  try {
    supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
    console.log("%cWealth Management: Supabase client initialized.", "color: #10b981; font-weight: bold;");
  } catch (e) {
    console.error("%cWealth Management: Supabase Client Init Error", "color: #ef4444; font-weight: bold;", e);
  }
}

interface User {
  id: string;
  email: string;
  name: string;
  metadata?: any;
}

export type AuthPhase = 'IDLE' | 'INITIALIZING' | 'FETCHING_SESSION' | 'SYNCING_PROFILE' | 'READY';

interface AuthResponse {
  success: boolean;
  error?: string;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, pass: string) => Promise<AuthResponse>;
  signup: (email: string, pass: string, name: string) => Promise<AuthResponse>;
  logout: () => void;
  resetPasswordForEmail: (email: string) => Promise<AuthResponse>;
  updatePassword: (newPass: string) => Promise<AuthResponse>;
  isRecoveryMode: boolean;
  setIsRecoveryMode: (val: boolean) => void;
  isLoading: boolean;
  isSyncing: boolean;
  configError: string | null;
  authPhase: AuthPhase;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [authPhase, setAuthPhase] = useState<AuthPhase>('IDLE');
  const syncInProgress = useRef<string | null>(null);

  const ensureProfile = async (sessionUser: any) => {
    if (!supabase) return { name: 'User' };
    
    // Prevent overlapping syncs for the same user
    if (syncInProgress.current === sessionUser.id) {
      console.log("Auth Lifecycle: Sync already in progress for this ID. Skipping.");
      return null;
    }
    syncInProgress.current = sessionUser.id;

    try {
      setAuthPhase('SYNCING_PROFILE');
      console.log(`%cAuth Lifecycle: Ensuring profile for ${sessionUser.email}`, "color: #3b82f6;");
      
      const fallbackName = sessionUser.user_metadata?.full_name || sessionUser.email?.split('@')[0] || 'User';

      // 3.5s safety timeout for the database call
      const profilePromise = supabase
        .from('profiles')
        .select('name')
        .eq('id', sessionUser.id)
        .maybeSingle();

      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Profile sync timed out')), 3500)
      );

      // @ts-ignore
      const { data: profile, error } = await Promise.race([profilePromise, timeoutPromise]);

      if (error) {
        console.warn("AuthContext: Profile fetch issue (Table might be missing or RLS blocked)", error);
        // Table not existing is code 42P01
      }

      if (!profile) {
        console.log("%cAuth Lifecycle: No profile found. Attempting to create...", "color: #f59e0b;");
        // We attempt a fire-and-forget upsert, we don't let it block the main boot if it takes too long
        supabase.from('profiles').upsert({ 
          id: sessionUser.id, 
          name: fallbackName 
        }).then(({ error: e }) => {
          if (e) console.warn("Auth Lifecycle: Profile upsert failed silently", e);
        });
        
        return { name: fallbackName };
      }
      
      return profile;
    } catch (err) {
      console.warn("AuthContext: Profile sync bypassed (Timeout or Unexpected Error)", err);
      return { name: sessionUser.email?.split('@')[0] || 'User' };
    } finally {
      syncInProgress.current = null;
    }
  };

  useEffect(() => {
    console.group("%cWealth Management: Auth System Booting", "color: #1e293b; font-weight: 800;");
    setAuthPhase('INITIALIZING');
    
    if (!SUPABASE_URL || !SUPABASE_URL.startsWith('http') || !supabase) {
      console.warn("Auth Lifecycle: Critical Config Error. Supabase URL missing or invalid.");
      setConfigError("Application database connection is not established.");
      setIsLoading(false);
      setAuthPhase('READY');
      console.groupEnd();
      return;
    }

    let isMounted = true;

    const checkSession = async () => {
      try {
        setAuthPhase('FETCHING_SESSION');
        console.log("Auth Lifecycle: Fetching initial session...");
        
        // Parse Hash parameters for recovery
        const hash = window.location.hash || '';
        const search = window.location.search || '';
        const isRecovery = hash.includes('type=recovery') || search.includes('type=recovery') || window.location.href.includes('type=recovery');
        if (isRecovery) {
          console.log("Auth Lifecycle: Detected recovery hash parameters, activating password reset mode.");
          setIsRecoveryMode(true);
          try {
            window.history.replaceState(null, '', window.location.pathname);
          } catch (e) {}
        }
        
        const { data: { session }, error } = await supabase!.auth.getSession();
        
        if (error) {
          console.error("Auth Lifecycle: Session retrieval error", error);
        }
        
        if (session?.user && isMounted) {
          const profile = await ensureProfile(session.user);
          if (isMounted && profile) {
            setUser({
              id: session.user.id,
              email: session.user.email!,
              name: profile.name,
              metadata: session.user.user_metadata
            });
            console.log("%cAuth Lifecycle: Session stabilized.", "color: #10b981; font-weight: bold;");
          }
        }
      } catch (err) {
        console.error("Auth Lifecycle: Boot process failed.", err);
      } finally {
        if (isMounted) {
          setIsLoading(false);
          setAuthPhase('READY');
          console.groupEnd();
        }
      }
    };

    checkSession();

    const { data: { subscription } } = supabase!.auth.onAuthStateChange(async (event, session) => {
      console.log(`%cAuth Event: ${event}`, "color: #8b5cf6; font-weight: bold;");
      
      if (!isMounted) return;
      
      if (event === 'PASSWORD_RECOVERY') {
        setIsRecoveryMode(true);
      }
      
      if (session) {
        const profile = await ensureProfile(session.user);
        if (isMounted && profile) {
          setUser({
            id: session.user.id,
            email: session.user.email!,
            name: profile.name,
            metadata: session.user.user_metadata
          });
          setIsLoading(false); // Safety
          setAuthPhase('READY');
        }
      } else {
        setUser(null);
        setIsLoading(false); // Safety
        setAuthPhase('READY');
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const login = async (email: string, pass: string): Promise<AuthResponse> => {
    if (!supabase) return { success: false, error: 'Database not initialized' };
    setIsSyncing(true);
    try {
      const cleanEmail = (email || '').trim().toLowerCase();
      const cleanPass = pass || '';
      if (!cleanEmail) return { success: false, error: 'Please enter your email address' };
      if (!cleanPass) return { success: false, error: 'Please enter your password' };
      const { error } = await supabase.auth.signInWithPassword({ email: cleanEmail, password: cleanPass });
      if (error) throw error;
      return { success: true };
    } catch (err: any) {
      console.error("Login process error:", err);
      let userFriendlyError = err.message || 'Unknown login error';
      if (err.message?.toLowerCase().includes('invalid login credentials')) {
        userFriendlyError = 'Invalid email or password. Please verify your credentials or use the reset option below.';
      } else if (err.message?.toLowerCase().includes('email not confirmed')) {
        userFriendlyError = 'Email not confirmed. Please check your inbox for the confirmation link from Supabase.';
      }
      return { success: false, error: userFriendlyError };
    } finally {
      setIsSyncing(false);
    }
  };

  const signup = async (email: string, pass: string, name: string): Promise<AuthResponse> => {
    if (!supabase) return { success: false, error: 'Database not initialized' };
    setIsSyncing(true);
    try {
      const cleanEmail = (email || '').trim().toLowerCase();
      const cleanPass = pass || '';
      const cleanName = (name || '').trim() || cleanEmail.split('@')[0] || 'User';
      const { data, error } = await supabase.auth.signUp({ 
        email: cleanEmail, 
        password: cleanPass,
        options: { data: { full_name: cleanName } }
      });
      if (error) throw error;
      if (data?.user) {
        // Explicitly create profile
        try {
          await supabase.from('profiles').upsert({ id: data.user.id, name: cleanName });
        } catch (pe) {
          console.warn("Profile creation on signup skipped:", pe);
        }
        return { success: true };
      }
      return { success: false, error: 'Sign up succeeded but user data was missing' };
    } catch (err: any) {
      console.error("Signup process error:", err);
      return { success: false, error: err.message || 'Unknown signup error' };
    } finally {
      setIsSyncing(false);
    }
  };

  const logout = async () => {
    if (supabase) {
      try {
        await supabase.auth.signOut();
      } catch (e) {
        console.warn("Logout error:", e);
      }
    }
    setUser(null);
  };

  const resetPasswordForEmail = async (email: string): Promise<AuthResponse> => {
    if (!supabase) return { success: false, error: 'Database not initialized' };
    setIsSyncing(true);
    try {
      const cleanEmail = (email || '').trim().toLowerCase();
      if (!cleanEmail) return { success: false, error: 'Please enter your email address' };
      const redirectTo = `${window.location.origin}${window.location.pathname}`;
      const { error } = await supabase.auth.resetPasswordForEmail(cleanEmail, { redirectTo });
      if (error) throw error;
      return { success: true };
    } catch (err: any) {
      console.error("Reset password process error:", err);
      return { success: false, error: err.message || 'Unknown reset error' };
    } finally {
      setIsSyncing(false);
    }
  };

  const updatePassword = async (newPass: string): Promise<AuthResponse> => {
    if (!supabase) return { success: false, error: 'Database not initialized' };
    setIsSyncing(true);
    try {
      const { data, error } = await supabase.auth.updateUser({ password: newPass });
      if (error) throw error;
      if (data?.user) {
        try {
          const fallbackName = data.user.user_metadata?.full_name || data.user.email?.split('@')[0] || 'User';
          await supabase.from('profiles').upsert({ id: data.user.id, name: fallbackName });
        } catch (pe) {
          console.warn("Profile update after password update skipped:", pe);
        }
      }
      return { success: true };
    } catch (err: any) {
      console.error("Update password process error:", err);
      return { success: false, error: err.message || 'Unknown update password error' };
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      signup, 
      logout, 
      resetPasswordForEmail, 
      updatePassword, 
      isRecoveryMode, 
      setIsRecoveryMode, 
      isLoading, 
      isSyncing, 
      configError, 
      authPhase 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export { supabase };